import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useHotkeys } from "react-hotkeys-hook";
import { HiOutlineSearch, HiOutlineX, HiOutlineChevronDown } from "react-icons/hi";
import DateInput from "./DateInput";
import { SHORTCUTS } from "../utils/shortcuts";
import { notifyShortcut } from "../utils/shortcutTip";
import { SEARCH_OPERATOR } from "../hooks/useSearchFilters";

const formatDateForDisplay = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`;
};

const SearchFilters = ({ fields, filters, onFiltersChange, search, onSearchChange, onKeyDown, placeholder = "Rechercher...", saveOptions, isLoading = false }) => {
  const [activeDropdown, setActiveDropdown] = useState(null); // { filterId, type: "field" | "operator" | "value" }
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const firstChipRef = useRef(null);
  const dropdownRef = useRef(null);
  const valueSearchRef = useRef(null);
  const pendingEditRef = useRef(null); // { filterId, editText } — set by FilterToken, read on close

  // useKey matches event.key ("/") so the shortcut fires whatever combo a layout uses to type "/".
  useHotkeys(SHORTCUTS.FOCUS_SEARCH.keys, () => inputRef.current?.focus(), { useKey: true, preventDefault: true });
  useHotkeys(SHORTCUTS.FOCUS_FILTER.keys, () => firstChipRef.current?.focus(), { preventDefault: true });

  const commitPendingEdit = useCallback(() => {
    if (!pendingEditRef.current) return;
    const { filterId, editText } = pendingEditRef.current;
    const parsed = editText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const unique = [...new Set(parsed)];
    onFiltersChange(filters.map((f) => (f.id === filterId ? { ...f, values: unique } : f)));
    pendingEditRef.current = null;
  }, [onFiltersChange, filters]);

  // Keep a stable ref to commitPendingEdit so click-outside listener doesn't re-register on every filter mutation
  const commitPendingEditRef = useRef(commitPendingEdit);
  useLayoutEffect(() => {
    commitPendingEditRef.current = commitPendingEdit;
  });

  // Click-outside to close dropdowns
  useEffect(() => {
    if (!activeDropdown) return;

    const handleMouseDown = (e) => {
      if (dropdownRef.current?.contains(e.target)) return;
      if (containerRef.current?.contains(e.target)) return;
      commitPendingEditRef.current();
      setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [activeDropdown]);

  // Focus value search input when value dropdown opens (needed because the input doesn't exist in DOM yet when addFilter sets the dropdown)
  useEffect(() => {
    if (activeDropdown?.type === "value" && valueSearchRef.current) {
      valueSearchRef.current.focus();
    }
  }, [activeDropdown]);

  const addFilter = (fieldKey) => {
    const fieldDef = fields.find((f) => f.key === fieldKey);
    const newFilter = {
      id: `${fieldKey}-${Date.now()}`,
      field: fieldKey,
      operator: fieldDef.operators[0],
      values: [],
    };
    onFiltersChange([...filters, newFilter]);
    setActiveDropdown({ filterId: newFilter.id, type: "value" });
  };

  const removeFilter = (filterId) => {
    onFiltersChange(filters.filter((f) => f.id !== filterId));
    setActiveDropdown(null);
    inputRef.current?.focus();
  };

  const updateFilterField = (filterId, newFieldKey) => {
    const fieldDef = fields.find((f) => f.key === newFieldKey);
    onFiltersChange(
      filters.map((f) => {
        if (f.id !== filterId) return f;
        return { ...f, field: newFieldKey, operator: fieldDef.operators[0], values: [] };
      }),
    );
    setActiveDropdown(null);
  };

  const updateFilterOperator = (filterId, operator) => {
    onFiltersChange(
      filters.map((f) => {
        if (f.id !== filterId) return f;
        // Date IS expects "YYYY-MM-DD"; WITHIN_LAST_DAYS expects a number — incompatible, reset.
        const wasWithin = f.operator === SEARCH_OPERATOR.WITHIN_LAST_DAYS;
        const willBeWithin = operator === SEARCH_OPERATOR.WITHIN_LAST_DAYS;
        const resetValues = wasWithin !== willBeWithin;
        return { ...f, operator, values: resetValues ? [] : f.values };
      }),
    );
    setActiveDropdown(null);
  };

  const closeDropdown = () => {
    commitPendingEdit();
    setActiveDropdown(null);
  };

  const toggleDropdown = (filterId, type) => {
    commitPendingEdit();
    const isAlreadyOpen = activeDropdown?.filterId === filterId && activeDropdown?.type === type;
    setActiveDropdown(isAlreadyOpen ? null : { filterId, type });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Escape" && activeDropdown) {
      e.preventDefault();
      setActiveDropdown(null);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Backspace" && search === "" && filters.length > 0) {
      e.preventDefault();
      removeFilter(filters[filters.length - 1].id);
      return;
    }
    onKeyDown?.(e);
  };

  const handleContainerClick = (e) => {
    if (e.target === containerRef.current || e.target.closest("[data-search-area]")) {
      if (activeDropdown) closeDropdown();
      inputRef.current?.focus();
      notifyShortcut("FOCUS_SEARCH");
    }
  };

  // Un filtre restauré (vue enregistrée / session) peut viser un champ absent : champs dérivés des données, ou champ conditionnel dont la requête n'a pas encore répondu.
  const getFieldDef = (fieldKey) => fields.find((f) => f.key === fieldKey) || { key: fieldKey, label: fieldKey, operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT] };

  const getAvailableValues = (fieldKey) => getFieldDef(fieldKey).values || [];

  return (
    <div className="border-b border-gray-100">
      {/* Search bar with tokens */}
      <div ref={containerRef} className="flex cursor-text flex-wrap items-center gap-1.5 px-4 py-2.5" onClick={handleContainerClick}>
        {isLoading ? (
          <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500" />
        ) : (
          <HiOutlineSearch className="h-4 w-4 flex-shrink-0 text-gray-400" />
        )}

        {filters.map((filter) => (
          <FilterToken
            key={filter.id}
            filter={filter}
            fieldDef={getFieldDef(filter.field)}
            availableValues={getAvailableValues(filter.field)}
            activeDropdown={activeDropdown}
            dropdownRef={dropdownRef}
            valueSearchRef={valueSearchRef}
            pendingEditRef={pendingEditRef}
            fields={fields}
            onEscape={() => {
              commitPendingEdit();
              setActiveDropdown(null);
            }}
            onFieldClick={() => toggleDropdown(filter.id, "field")}
            onFieldSelect={(fieldKey) => updateFilterField(filter.id, fieldKey)}
            onOperatorClick={() => toggleDropdown(filter.id, "operator")}
            onValueClick={() => toggleDropdown(filter.id, "value")}
            onOperatorSelect={(op) => updateFilterOperator(filter.id, op)}
            onSetValues={(nextValues) => onFiltersChange(filters.map((f) => (f.id === filter.id ? { ...f, values: nextValues } : f)))}
            onRemove={() => removeFilter(filter.id)}
          />
        ))}

        <div data-search-area className="min-w-[120px] flex-grow">
          <input
            ref={inputRef}
            type="text"
            placeholder={filters.length === 0 ? placeholder : ""}
            value={search}
            onChange={onSearchChange}
            onKeyDown={handleInputKeyDown}
            className="w-full bg-transparent py-1.5 text-sm outline-none placeholder:text-gray-400"
          />
        </div>
        {filters.some((f) => f.values.length > 0) && saveOptions?.length > 0 && (
          <div className="flex flex-shrink-0 items-center gap-2">{saveOptions?.length > 0 && <SaveButton options={saveOptions} />}</div>
        )}
      </div>

      {/* Suggestion chips — always visible */}
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-1.5">
        {fields.map((field, idx) => (
          <button
            key={field.key}
            ref={idx === 0 ? firstChipRef : null}
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={() => addFilter(field.key)}
          >
            {field.label}
            <HiOutlineChevronDown className="h-3 w-3 text-gray-400" />
          </button>
        ))}
      </div>
    </div>
  );
};

const DropdownList = ({ items, activeValue, onSelect, anchorRef, dropdownRef }) => (
  <DropdownPortal anchorRef={anchorRef} dropdownRef={dropdownRef} align="left">
    <div className="py-1">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-gray-50 ${activeValue === item.key ? "bg-gray-100 font-medium text-gray-900" : "text-gray-600"}`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(item.key);
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  </DropdownPortal>
);

const FilterToken = ({
  filter,
  fieldDef,
  availableValues,
  activeDropdown,
  dropdownRef,
  valueSearchRef,
  pendingEditRef,
  fields,
  onEscape,
  onFieldClick,
  onFieldSelect,
  onOperatorClick,
  onValueClick,
  onOperatorSelect,
  onSetValues,
  onRemove,
}) => {
  const fieldRef = useRef(null);
  const operatorRef = useRef(null);
  const valueRef = useRef(null);
  const [editText, setEditText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isFieldOpen = activeDropdown?.filterId === filter.id && activeDropdown?.type === "field";
  const isOperatorOpen = activeDropdown?.filterId === filter.id && activeDropdown?.type === "operator";
  const isValueOpen = activeDropdown?.filterId === filter.id && activeDropdown?.type === "value";
  const isDateField = fieldDef.type === "date";
  const isWithinLastDays = filter.operator === SEARCH_OPERATOR.WITHIN_LAST_DAYS;

  useEffect(() => {
    if (!isValueOpen) return;
    const initial = filter.values.length > 0 ? filter.values.join(",") + "," : "";
    setEditText(initial);
    setSelectedIndex(0);
  }, [isValueOpen, filter.values]);

  // Derive search term and filter values locally
  const { searchTerm, filterValues } = useMemo(() => {
    const parts = editText.split(",");
    return { filterValues: parts, searchTerm: parts[parts.length - 1].trim() };
  }, [editText]);

  const filteredValues = useMemo(() => {
    if (!searchTerm) return availableValues;
    const searchLower = searchTerm.toLowerCase();
    const valueLabels = fieldDef.valueLabels || {};
    return availableValues.filter((v) => {
      const label = valueLabels[v] || v;
      return v.toLowerCase().includes(searchLower) || label.toLowerCase().includes(searchLower);
    });
  }, [availableValues, searchTerm, fieldDef.valueLabels]);

  // Build dropdown items: suggestions first, custom at end
  const dropdownItems = useMemo(() => {
    const items = filteredValues.map((v) => ({ type: "suggestion", value: v }));
    if (searchTerm && !filteredValues.includes(searchTerm)) {
      items.push({ type: "custom", value: searchTerm });
    }
    return items;
  }, [filteredValues, searchTerm]);

  // Reset selectedIndex when dropdown items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [dropdownItems.length]);

  // Sync editText to pendingEditRef so parent can commit on close
  useEffect(() => {
    if (isValueOpen) {
      pendingEditRef.current = { filterId: filter.id, editText };
    }
  }, [editText, isValueOpen, filter.id, pendingEditRef]);

  const selectItem = (value) => {
    const committedValues = filterValues.slice(0, -1).filter(Boolean);

    if (committedValues.includes(value)) {
      const remaining = committedValues.filter((v) => v !== value);
      setEditText(remaining.length > 0 ? remaining.join(",") + "," : "");
    } else {
      committedValues.push(value);
      setEditText(committedValues.join(",") + ",");
    }
    valueSearchRef.current?.focus();
  };

  const handleEditKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, dropdownItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEscape();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (dropdownItems.length > 0 && selectedIndex >= 0) {
        selectItem(dropdownItems[selectedIndex].value);
      }
    }
  };

  // Auto-size input based on character count
  const inputWidth = Math.max(editText.length + 1, 1);

  const labelFor = (v) => {
    if (isWithinLastDays === true) return `${v} jour${Number(v) > 1 ? "s" : ""}`;
    if (isDateField === true) return formatDateForDisplay(v);
    return fieldDef.valueLabels?.[v] || v;
  };

  const valueDisplay = () => {
    if (filter.values.length === 0) return <span className="text-gray-400">...</span>;
    return filter.values.map((v, i) => (
      <React.Fragment key={v}>
        {i > 0 && <span className="font-medium text-gray-500"> ou </span>}
        <span className="font-medium text-primary-500">{labelFor(v)}</span>
      </React.Fragment>
    ));
  };

  return (
    <div className="inline-flex items-center rounded-md border border-gray-300 bg-white text-xs">
      {/* Field label (clickable to change field) */}
      <button ref={fieldRef} type="button" className="rounded-l-md py-1 pl-1 font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={onFieldClick}>
        {fieldDef.label}
      </button>

      {/* Operator (clickable) */}
      <button ref={operatorRef} type="button" className="px-1 py-1 font-medium text-gray-500 transition-colors hover:bg-gray-50" onClick={onOperatorClick}>
        {filter.operator}
      </button>

      {/* Value — inline input when editing, static display otherwise */}
      {isDateField === true && isWithinLastDays === true ? (
        <div ref={valueRef} className="flex items-baseline gap-1 py-1 pr-1">
          <input
            type="number"
            min={1}
            max={365}
            value={filter.values[0] || ""}
            onChange={(e) => {
              const next = e.target.value.replace(/[^0-9]/g, "");
              onSetValues(next ? [next] : []);
            }}
            placeholder="7"
            className="bg-transparent text-xs font-medium text-primary-500 outline-none [field-sizing:content] placeholder:font-normal placeholder:text-gray-400 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <span className="text-xs text-gray-500">jours</span>
        </div>
      ) : isDateField === true ? (
        <div ref={valueRef} className="flex w-[80px] items-center">
          <DateInput
            value={filter.values[0] || ""}
            onChange={(nextDate) => {
              if (nextDate === null) return;
              onSetValues(nextDate ? [nextDate] : []);
            }}
            className="!h-auto !rounded-none !border-0 !bg-transparent !py-1 !pl-0 !pr-1 !text-xs !font-medium !text-primary-500 !ring-0 !ring-offset-0 transition-colors placeholder:!font-normal placeholder:!text-gray-400 hover:!bg-primary-50 focus:!outline-none focus:!ring-0 focus:!ring-offset-0"
            placeholder=""
          />
        </div>
      ) : isValueOpen ? (
        <div ref={valueRef} className="flex items-center py-1 pr-1">
          <input
            ref={valueSearchRef}
            type="text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={handleEditKeyDown}
            className="bg-transparent text-xs outline-none"
            style={{ width: `${inputWidth}ch` }}
          />
        </div>
      ) : (
        <button
          ref={valueRef}
          type="button"
          className="max-w-[250px] truncate py-1 pr-1 transition-colors hover:bg-primary-50"
          onClick={onValueClick}
          title={filter.values.join(", ")}
        >
          {valueDisplay()}
        </button>
      )}

      {/* Remove button */}
      <button type="button" className="px-1 py-1 text-gray-400 transition-colors hover:text-gray-600" onClick={onRemove}>
        <HiOutlineX className="h-3 w-3" />
      </button>

      {/* Field dropdown */}
      {isFieldOpen && (
        <DropdownList
          items={fields.map((f) => ({ key: f.key, label: f.label }))}
          activeValue={filter.field}
          onSelect={onFieldSelect}
          anchorRef={fieldRef}
          dropdownRef={dropdownRef}
        />
      )}

      {/* Operator dropdown */}
      {isOperatorOpen && (
        <DropdownList
          items={fieldDef.operators.map((op) => ({ key: op, label: op }))}
          activeValue={filter.operator}
          onSelect={onOperatorSelect}
          anchorRef={operatorRef}
          dropdownRef={dropdownRef}
        />
      )}

      {/* Value dropdown */}
      {isValueOpen && isDateField !== true && (
        <DropdownPortal anchorRef={valueRef} dropdownRef={dropdownRef} align="left" width={220}>
          <div className="max-h-[200px] overflow-y-auto py-1">
            {dropdownItems.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Aucun resultat</div>}
            {dropdownItems.map((item, index) => {
              const isHighlighted = index === selectedIndex;
              return (
                <label
                  key={`${item.type}-${item.value}`}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-gray-600 transition-colors ${isHighlighted ? "bg-primary-100" : "hover:bg-primary-50"}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    selectItem(item.value);
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <input type="checkbox" checked={filterValues.includes(item.value)} readOnly className="pointer-events-none h-3 w-3 rounded border-gray-300 accent-primary-500" />
                  <span className="truncate">
                    {item.type === "custom" ? (
                      <>
                        &quot;{item.value}&quot; <span className="text-gray-400">(valeur personnalisee)</span>
                      </>
                    ) : (
                      labelFor(item.value)
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="border-t border-gray-100 px-3 py-2 text-[10px] leading-relaxed text-gray-400">
            <p>Cliquer pour selectionner plusieurs valeurs</p>
            <p>Taper pour filtrer les suggestions</p>
            <p>Entree pour ajouter une valeur personnalisee</p>
          </div>
        </DropdownPortal>
      )}
    </div>
  );
};

const SaveButton = ({ options }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  if (options.length === 1) {
    return (
      <button type="button" className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700" onClick={options[0].onClick}>
        Enregistrer
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex items-center gap-1 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700"
        onClick={() => setOpen((prev) => !prev)}
      >
        Enregistrer
        <HiOutlineChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div ref={menuRef} className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {options.map((option, index) => (
            <button
              key={option.label}
              type="button"
              className={`w-full px-3 py-2 text-left text-sm text-gray-600 transition-colors hover:bg-gray-50 ${index === 0 ? "rounded-t-lg" : ""} ${index === options.length - 1 ? "rounded-b-lg" : ""}`}
              onClick={() => {
                setOpen(false);
                option.onClick();
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownPortal = ({ anchorRef, dropdownRef, align, width, children }) => {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const top = rect.bottom + 4;
    const left = align === "right" ? rect.right - (width || rect.width) : rect.left;
    setPosition({ top, left, minWidth: width || rect.width });
  }, [align, width, anchorRef]);

  if (!position) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-50 rounded-md border border-gray-200 bg-white shadow-lg"
      style={{ top: position.top, left: position.left, minWidth: position.minWidth }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default SearchFilters;
